import React, { useEffect, useContext, useRef } from "react";
import PropTypes from "prop-types";
import qapi from "views_shared/app/javascript/api";
import { companyUrl } from "views_shared/app/javascript/utils/urls";
import ProfilePage from "../../profiles/ProfilePage";
import useProfileCompletion from "../../profiles/ChecklistCard/use_profile_completion";
import CompanySections from "./company_sections";
import CompanyHeader from "./company_header";
import Sidebar from "./sidebar";
import EditModal from "../edit/edit_modal";
import { CompanyContext } from "../data/company_context_management";
import useCompanyPeople from "../data/use_company_people";
import ShowPageSkeleton from "./show_page_skeleton";

// Custom hook to get the previous value
function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

function CompanyShow({ companyId, userId = undefined }) {
  const { setCompanyId, topLevelIsLoading, asyncError, company, viewerContext } = useContext(CompanyContext);
  const editable = !!viewerContext?.editable;

  const currentSlug = company.slug;
  const previousSlug = usePrevious(currentSlug);

  useEffect(() => {
    // V1-COMPAT: on V1 the equivalent page is the id-based /companies/:id (see companyUrl in
    // views_shared/utils/urls), which a slug change never affects, so there is nothing to redirect.
    if (globalThis.Qwoted?.V1_HOSTED) return;
    if (previousSlug && currentSlug && previousSlug !== currentSlug) {
      window.location.href = companyUrl({ slug: currentSlug, migrate_id: company.migrate_id });
    }
  }, [previousSlug, currentSlug, company.migrate_id]);

  useEffect(() => {
    setCompanyId(companyId);
  }, [companyId, setCompanyId]);

  // Let top-level ErrorBoundary see any async errors
  if (asyncError) throw asyncError;

  // Owner/admin-only widget: idling the hook (rather than just gating the render) for a non-editable
  // viewer means it never even issues the read. A collapsed card still renders its header, so it
  // still counts as rail content; a hidden one doesn't.
  const checklist = useProfileCompletion({
    resourceClient: qapi.companyCompletionScores,
    id: editable ? company?.id : null,
    kind: "company",
    refreshKey: company,
  });
  const checklistContributes = editable && !checklist.hidden && (checklist.isLoading || !!checklist.score);

  // Contact details relocated out of the header into this Connect rail card (the header's actions
  // row carries View Website/Share Profile instead). No Message button here yet: most companies
  // have no representative to receive the message, so the affordance stays off until a
  // reachable-recipient check exists to gate it (render only when the company resolves to at least
  // one recipient).
  const contactDetails = company.contact_details || [];
  const connectActions = null;
  const connectHasContent = contactDetails.length > 0 || connectActions != null;

  // The company's own current team roster, deduped to people (see useCompanyPeople) — backs the
  // rail's "People (N Users)" card.
  const people = useCompanyPeople(companyId);

  // Does the right rail have any content? The Sidebar renders the "Claim this page" box only for
  // anonymous viewers, and the PR Team / PR Agencies lists only for the "default" subtype — that half
  // of the gate is unchanged and known *synchronously* once the company has loaded (no async fetch
  // involved), so collapsing on it alone causes no reflow. What changed: an authenticated viewer of
  // a non-default company (e.g. a pr_firm) used to get a guaranteed-empty rail regardless of the
  // Checklist/Connect/People cards actually having something to show. The gate now also asks those
  // cards directly — collapse-when-empty, the same posture the person rails use — so a pr_firm
  // viewer gets a rail whenever any of them does.
  //
  // Two async-emptiness transients are accepted deliberately rather than engineered away: the
  // "default company that happens to have no PR team" case keeps its always-reserved rail (as it
  // did before), and a pr_firm whose Checklist/Connect settle empty shows a brief rail skeleton
  // while People loads, collapsing to full width only if that also comes back empty. Both
  // alternatives are worse for the common populated case: holding first paint until People settles
  // delays every company page, and appearing the rail late reflows in the other direction.
  const railHasContent =
    !userId ||
    company.displayType === "default" ||
    checklistContributes ||
    connectHasContent ||
    people.isLoading ||
    people.members.length > 0;

  // Sections are now a stacked, always-visible list of <SectionCard>s (no tabs), so the
  // old activeTab state machine + tab-switching effects are gone. Section visibility
  // (pr_firm conditionals, About-when-present) lives in <CompanySections>; the section-
  // hidden / "Add a section" logic stays in the provider + header.
  return (
    <div>
      <EditModal />

      <ProfilePage
        isLoading={topLevelIsLoading}
        skeleton={<ShowPageSkeleton railHasContent={railHasContent} />}
        header={<CompanyHeader />}
        rightRail={
          railHasContent ? (
            <Sidebar
              userId={userId}
              checklist={checklist}
              checklistContributes={checklistContributes}
              contactDetails={contactDetails}
              connectActions={connectActions}
              connectHasContent={connectHasContent}
              people={people}
            />
          ) : null
        }
      >
        <CompanySections />
      </ProfilePage>
    </div>
  );
}

CompanyShow.propTypes = {
  companyId: PropTypes.string.isRequired,
  userId: PropTypes.string,
};

export default CompanyShow;
